import { type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes, useRef, useState } from 'react';
import { cn } from './cn';

/**
 * Spreadsheet-like grid (Plan / Fact timesheets, FR-TS-001): grouped headers, editable cells,
 * computed and total cells. Features compose these parts — they never draw a raw <table> or
 * <input>. Values are plain text; the feature parses and formats them.
 */

export function Sheet({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-card border border-border bg-surface">
      <table aria-label={label} className="w-max min-w-full border-collapse text-sm tabular-nums">
        {children}
      </table>
    </div>
  );
}

export const SheetHead = ({ children }: { children: ReactNode }) => (
  <thead className="bg-canvas/70 text-xs text-ink-muted">{children}</thead>
);
export const SheetBody = ({ children }: { children: ReactNode }) => <tbody>{children}</tbody>;

export type SheetRowKind = 'plain' | 'total' | 'subtle';

/** `rowKey` is exposed as `data-row` (tests address rows by it). */
export function SheetRow({
  kind = 'plain',
  rowKey,
  children,
}: {
  kind?: SheetRowKind;
  rowKey?: string;
  children: ReactNode;
}) {
  return (
    <tr
      data-row={rowKey}
      className={cn(
        'border-t border-border',
        kind === 'total' && 'bg-canvas/60 font-semibold',
        kind === 'subtle' && 'text-ink-muted',
      )}
    >
      {children}
    </tr>
  );
}

export type SheetCellTone = 'plain' | 'computed' | 'locked' | 'total' | 'positive' | 'negative';

const TONES: Record<SheetCellTone, string> = {
  plain: '',
  computed: 'text-ink-muted',
  locked: 'bg-slate-100 text-ink-muted',
  total: 'bg-canvas font-semibold',
  positive: 'text-emerald-700',
  negative: 'text-red-600',
};

/** Header cell; `sticky` keeps the description column visible while scrolling. */
export function SheetTh({
  sticky,
  tone = 'plain',
  className,
  ...th
}: ThHTMLAttributes<HTMLTableCellElement> & { sticky?: boolean; tone?: SheetCellTone }) {
  return (
    <th
      scope="col"
      className={cn(
        'whitespace-nowrap border-l border-border px-2 py-1.5 text-center font-medium first:border-l-0',
        sticky && 'sticky left-0 z-10 bg-canvas text-left',
        TONES[tone],
        className,
      )}
      {...th}
    />
  );
}

export function SheetTd({
  sticky,
  tone = 'plain',
  align = 'right',
  className,
  ...td
}: TdHTMLAttributes<HTMLTableCellElement> & {
  sticky?: boolean;
  tone?: SheetCellTone;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      className={cn(
        'whitespace-nowrap border-l border-border px-2 py-1 first:border-l-0',
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
        sticky && 'sticky left-0 z-10 bg-surface',
        TONES[tone],
        className,
      )}
      {...td}
    />
  );
}

/**
 * Editable cell value. Keeps what is typed while focused and hands it over on blur or Enter
 * (`onCommit`); arrow keys and the mouse wheel never change it (FR-TS-017). `value` from outside
 * replaces the text when the cell is not being edited (undo, reload).
 */
export function CellInput({
  label,
  value,
  onCommit,
  readOnly,
  align = 'right',
  width = 'w-20',
  placeholder,
  list,
}: {
  label: string;
  value: string;
  onCommit: (text: string) => void;
  readOnly?: boolean;
  align?: 'left' | 'right';
  width?: string;
  placeholder?: string;
  /** id of a <datalist> with suggestions. */
  list?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const cancelled = useRef(false);
  return (
    <input
      type="text"
      inputMode={align === 'right' ? 'decimal' : 'text'}
      aria-label={label}
      title={label}
      value={draft ?? value}
      readOnly={readOnly}
      placeholder={placeholder}
      list={list}
      onFocus={() => !readOnly && setDraft(value)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (!cancelled.current && draft !== null && draft !== value) onCommit(draft);
        cancelled.current = false;
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') cancelled.current = true;
        if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
      }}
      className={cn(
        'rounded border border-transparent bg-transparent px-1.5 py-1 hover:border-border',
        'focus:border-brand-500 focus:bg-surface focus:outline-none',
        align === 'right' ? 'text-right' : 'text-left',
        readOnly && 'cursor-default hover:border-transparent',
        width,
      )}
    />
  );
}
